import React, { useEffect, useRef, useState } from "react";
/* eslint import/no-webpack-loader-syntax: off */
import mapboxgl from "!mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Row, Col } from "react-bootstrap";
import { Select, Spin, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

/* ---------------- AUTH ---------------- */

const AUTH_URL = "https://devapi.intensel.net/api/v2/setup_map_auth/";

/* ---------------- VARIABLE INFO ---------------- */

const VARIABLE_INFO = {
  rainfall_flood: {
    title: "Rainfall Flood",
    tooltip: {
      description:
        "District map represents average flood depth using zonal statistics (100-year return period).",
      scale: [
        "< 0 → Very Low",
        "0 – 0.5 → Low",
        "0.5 – 1 → Moderate",
        "1-2  → High",
        "2 –5 → Very High",
        "> 5 → Extreme",
      ],
      extra: ["Observed flood color code: Blue → Red", "Units: meters (m)"],
    },
  },

  river_flood: {
    title: "River Flood",
    tooltip: {
      description:
        "District map represents average flood depth using zonal statistics.",
      scale: [
        "< 0 → Very Low",
        "0 – 0.5 → Low",
        "0.5 – 1 → Moderate",
        "1 – 2 → High",
        "2 – 5 → Very High",
        "> 5 → Extreme",
      ],
      extra: ["Units: meters (m)"],
    },
  },

  storm_surge: {
    title: "Storm Surge",
    tooltip: {
      description: "Flood inundation due to storm surge.",
      scale: [
        "< 0 → Very Low",
        "0 – 0.5 → Low",
        "0.5 – 1 → Moderate",
        "1 – 2 → High",
        "2 –5 → Very High",
        "> 5 → Extreme",
      ],
      extra: ["Units: meters (m)"],
    },
  },

  extreme_heat: {
    title: "Extreme Heat",
    tooltip: {
      description: "Temperature risk classification (°C).",
      scale: [
        "< 27 → No Risk",
        "27–31 → Low Risk",
        "31–40 → Moderate Risk",
        "40–53 → High Risk",
        "> 53 → Extreme Risk",
      ],
    },
  },

  extreme_cold: {
    title: "Extreme Cold",
    tooltip: {
      description: "Temperature risk classification (°C).",
      scale: [
        "< -10 → Extreme Risk",
        "-10 – -5 → Very High Risk",
        "-5 – 0 → High Risk",
      ],
    },
  },
};

/* ---------------- LEGENDS ---------------- */

const LEGENDS = {
  default: {
    unit: "m",
    items: [
      { color: "#33C2FF", label: "Very Low" },
      { color: "#78FFC9", label: "Low" },
      { color: "#E0FF55", label: "Moderate" },
      { color: "#FFD600", label: "High" },
      { color: "#FF8700", label: "Very High" },
      { color: "#FF2F00", label: "Extreme" },
    ],
  },
};

/* ---------------- VARIABLES ---------------- */

const VARIABLES = {
  extreme_cold: {
    label: "Extreme Cold",
    url:
      "https://devmap.intensel.net/district/extreme_cold/tile/{x}/{y}/{z}.pbf",
  },
  extreme_heat: {
    label: "Extreme Heat",
    url:
      "https://devmap.intensel.net/district/extreme_heat/tile/{x}/{y}/{z}.pbf",
  },
  river_flood: {
    label: "River Flood",
    url:
      "https://devmap.intensel.net/district/river_flood/tile/{x}/{y}/{z}.pbf",
  },
  storm_surge: {
    label: "Storm Surge",
    url:
      "https://devmap.intensel.net/district/storm_surge/tile/{x}/{y}/{z}.pbf",
  },
  rainfall_flood: {
    label: "Rainfall Flood",
    url:
      "https://devmap.intensel.net/district/rainfall_flood/tile/{x}/{y}/{z}.pbf",
  },
};

/* ---------------- HELPERS ---------------- */

const VARIABLE_VALUE_KEYS = {
  extreme_cold: "Extreme Cold SSP 8.5 2050",
  extreme_heat: "Extreme Heat SSP 8.5 2050",
  rainfall_flood: "1:250",
  river_flood: "River Flood SSP 8.5 2050",
  storm_surge: "Storm Surge SSP 8.5 2050",
};

const getValueExpression = (variable) => [
  "coalesce",
  ["to-number", ["get", VARIABLE_VALUE_KEYS[variable]]],
  0,
];

const getLayerFilter = (variable) => {
  const v = getValueExpression(variable);

  if (
    variable === "rainfall_flood" ||
    variable === "storm_surge" ||
    variable === "river_flood"
  ) {
    return [">=", v, 0.28];
  }

  if (variable === "extreme_cold") {
    return ["<=", v, 0];
  }

  return true;
};

const getDistrictFillColor = (variable) => {
  const v = getValueExpression(variable);

  switch (variable) {
    case "extreme_cold":
      return ["step", v, "#E6F2FF", -10, "#B3DAFF", -5, "#80C1FF"];

    case "extreme_heat":
      return [
        "step",
        v,
        "#FFE5E5",
        27,
        "#FFB3B3",
        31,
        "#FF8080",
        40,
        "#FF4D4D",
      ];

    default:
      return [
        "step",
        v,
        "#336DFF",
        0.28,
        "#33C2FF",
        0.5,
        "#78FFC9",
        0.75,
        "#E0FF55",
        1,
        "#FFD600",
        1.5,
        "#FF8700",
        2,
        "#FF2F00",
      ];
  }
};

/* ---------------- COMPONENT ---------------- */

export default function DistrictMap() {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const [token] = useState(() => localStorage.getItem("token"));
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [selectedVar, setSelectedVar] = useState("extreme_cold");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    const setupAuth = async () => {
      try {
        const res = await fetch(AUTH_URL, {
          method: "POST",
          headers: { Authorization: `Token ${token}` },
        });

        if (!res.ok) throw new Error("Auth failed");

        setIsAuthReady(true);
      } catch (err) {
        console.error("Auth error:", err);
      }
    };

    setupAuth();
  }, [token]);

  useEffect(() => {
    if (!token || !isAuthReady) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v10",
      center: [78.9629, 20.5937],
      zoom: 3,
      transformRequest: (url) => {
        if (url.includes("devmap.intensel.net")) {
          return {
            url,
            headers: { Authorization: `Token ${token}` },
          };
        }
        return { url };
      },
    });

    mapRef.current = map;

    map.on("load", () => {
      map.addSource("source", {
        type: "vector",
        tiles: [VARIABLES[selectedVar].url],
      });

      map.addLayer({
        id: "layer",
        type: "fill",
        source: "source",
        "source-layer": "data",
        paint: {
          "fill-color": getDistrictFillColor(selectedVar),
          "fill-opacity": 0.6,
        },
        filter: getLayerFilter(selectedVar),
      });

      setLoading(false);
    });

    return () => map.remove();
  }, [token, isAuthReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (map.getLayer("layer")) map.removeLayer("layer");
    if (map.getSource("source")) map.removeSource("source");

    map.addSource("source", {
      type: "vector",
      tiles: [VARIABLES[selectedVar].url],
    });

    map.addLayer({
      id: "layer",
      type: "fill",
      source: "source",
      "source-layer": "data",
      paint: {
        "fill-color": getDistrictFillColor(selectedVar),
        "fill-opacity": 0.6,
      },
      filter: getLayerFilter(selectedVar),
    });
  }, [selectedVar]);

  const renderTooltip = () => {
    const data = VARIABLE_INFO[selectedVar]?.tooltip;
    if (!data) return null;

    return (
      <div>
        {data.description && <div>{data.description}</div>}
        {data.scale && data.scale.map((s, i) => <div key={i}>{s}</div>)}
        {data.extra && data.extra.map((e, i) => <div key={i}>• {e}</div>)}
      </div>
    );
  };

  if (!token) return <div>Please login to view map</div>;

  return (
    <div style={{ height: "100vh", position: "relative" }}>
      {/* INFO */}
      <div style={{ position: "absolute", top: 20, left: 20, zIndex: 10 }}>
        <div style={{ background: "#fff", padding: 10 }}>
          <strong>{VARIABLE_INFO[selectedVar]?.title}</strong>
          <Tooltip title={renderTooltip()}>
            <InfoCircleOutlined style={{ marginLeft: 6 }} />
          </Tooltip>
        </div>
      </div>

      {/* SELECT */}
      <div style={{ position: "absolute", top: 20, right: 20, zIndex: 10 }}>
        <Select
          value={selectedVar}
          onChange={setSelectedVar}
          options={Object.entries(VARIABLES).map(([k, v]) => ({
            label: v.label,
            value: k,
          }))}
        />
      </div>

      {/* LEGEND */}
      <div
        style={{
          position: "absolute",
          bottom: 200,
          right: 20,
          background: "#fff",
          padding: 10,
          zIndex: 10,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 6 }}>
          {`${selectedVar.toUpperCase()} (${
            selectedVar === "extreme_heat" || selectedVar === "extreme_cold"
              ? "°C"
              : LEGENDS.default.unit
          })`}
        </div>

        {(selectedVar === "extreme_heat"
          ? [
              { color: "#FFE5E5", label: "< 27" },
              { color: "#FFB3B3", label: "27–31" },
              { color: "#FF8080", label: "31–40" },
              { color: "#FF4D4D", label: "> 40" },
            ]
          : selectedVar === "extreme_cold"
          ? [
              { color: "#E6F2FF", label: "> -5" },
              { color: "#B3DAFF", label: "-10 – -5" },
              { color: "#80C1FF", label: "< -10" },
            ]
          : LEGENDS.default.items
        ).map((item, i) => (
          <div key={i} style={{ display: "flex", marginBottom: 4 }}>
            <div
              style={{
                width: 16,
                height: 16,
                background: item.color,
                marginRight: 8,
                zIndex: 10,
              }}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {loading && <Spin style={{ position: "absolute", top: "50%" }} />}

      <Row>
        <div ref={mapContainerRef} style={{ height: "800px", width: "100%" }} />
      </Row>
    </div>
  );
}
